/**
 * This file manages all of the state that is passed out to the adapters. The adapters don't modify their own state, they use this object to set/get LoggerService state.
 */
import { CentralLoggerState } from "../../types";
import debounce from "lodash/debounce";
import _get from "lodash/get";
import _set from "lodash/set";
import { LogLevels } from "../../types";

export const defaultLogLevels: LogLevels = {
	Error: true,
	Warn: true,
	Log: true,
	Info: false,
	Debug: false,
	Verbose: false,
	LocalOnly: false,
};
export class State {
	data: CentralLoggerState = {
		logState: {
			Error: true,
			Warn: true,
			Info: true,
			Log: true,
			Debug: true,
			Verbose: true,
		},
		clientState: {},
		showClientState: {},
		plainTextConsole: true,
		filterHighlights: true,
		highlightString: [
			{
				str: "",
			},
			{
				str: "",
			},
			{
				str: "",
			},
			{
				str: "",
			},
		],
		hideInactiveState: false,
		currentCategory: "system",
		devModeState: true,
		systemModeState: true,
		perfModeState: true,
		initialClientStateDefault: defaultLogLevels,
		showAdvancedViewFilters: false,
		wrapLog: false,
		showStackStraceInLog: false,
		filter: {
			logic: "OR",
		},
		showTimeElapsedFromStartup: true,
		colDefs: [
			{
				field: "timeElapsedFromStartup",
				name: "Time",
			},
			{
				field: "previousRowTimeDelta",
				name: "Row Delta",
			},
			{
				field: "category",
				name: "Category",
			},
		],
		visibleCols: ["timeElapsedFromStartup"],
		clientListVisible: true,
		isPersisted: false,
	};

	constructor() {
		let persistedState: any = {};
		const storageData = localStorage.getItem("finsemble.loggerStore.mainkey");
		if (storageData) {
			try {
				persistedState = JSON.parse(storageData);
			} catch (e) {
				persistedState = {};
			}
		}

		// Set current state to defaults, which are then overridden by persisted state
		this.data = {
			...this.data,
			...persistedState,
		};

		// Next, add in certain overrides that we always want on boot. Particularly,
		// possible missing values such as highlightString.
		this.data = {
			...this.data,
			filterHighlights: true,
			highlightString: this.data.highlightString ?? [],
			filter: this.data.filter ?? { logic: "OR" },
			clientListVisible: this.data.clientListVisible ?? true,
		};

		this.persist = debounce(this.persist.bind(this), 500);
	}

	toggle(key: string): void {
		const val: boolean = this.get(key);
		this.set(key, !val);
	}

	get(key: string): any {
		return _get(this.data, key);
	}

	set(key: string, value: any): void {
		_set(this.data, key, value);
		this.persist();
	}

	persist(): void {
		try {
			if (this.data) localStorage.setItem("finsemble.loggerStore.mainkey", JSON.stringify(this.data));
		} catch (err) {
			console.error(
				"Error saving logger state to local storage",
				err,
				`key="finsemble.loggerStore.mainkey"`,
				"value=",
				this.data
			);
		}
	}
}
